import { get } from 'lodash';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { showWorkflowErrorToast } from '@containers/workflows/helpers';
import {
  getWorkflowCandidateListAction,
  getWorkflowCandidateListErrorAction,
  getWorkflowCandidateListSuccessAction,
  getWtfWorkflowAction,
  getWtfWorkflowErrorAction,
  getWtfWorkflowSuccessAction,
} from '@containers/workflows/slice';
import { FormatedWorkflowList, Workflow, WorkflowCandidateList } from '@containers/workflows/types';
import fetchData from '@utils/fetchData';

import { GET_WTF_CANDIDATE_LIST_QUERY, GET_WTF_WORKFLOW_QUERY } from './queries';

interface GetWorkFlowListAction {
  type: string;
  payload: {
    courseId: number;
    searchConditions: Record<string, unknown>[] | Record<string, unknown>;
    limit: number;
    offset: number;
    conditions: Record<string, unknown>;
    callback?: {
      onSuccess: (res: unknown) => void;
    };
  };
}

interface GetWorkFlowCandidateList {
  type: string;
  payload: {
    workflow_id: number;
    step_id: number;
  };
}

export function* getWorkFlowListSaga(_action: GetWorkFlowListAction) {
  const { limit, offset, searchConditions, conditions, courseId, callback } = _action.payload;
  const queryVariables = {
    course_id: courseId,
    ...(limit && { limit: limit }),
    ...(offset && { offset: offset }),
    searchExpression: searchConditions,
    conditions: conditions,
  };
  try {
    const response: Workflow[] =
      // @ts-ignore - redux-saga types
      yield call(fetchData, {
        queryString: GET_WTF_WORKFLOW_QUERY,
        queryKey: 'wft_workflow',
        queryVariables: queryVariables,
        forceRefresh: true,
      });
    const aggregateResponse: {
      aggregate: { count: number };
      // @ts-ignore - redux-saga types
    } = yield call(fetchData, {
      queryString: GET_WTF_WORKFLOW_QUERY,
      queryKey: 'wft_workflow_aggregate',
      queryVariables: queryVariables,
      forceRefresh: true,
    });
    const count = get(aggregateResponse, 'aggregate.count', 0);
    if (callback && response && response.length > 0 && callback.onSuccess) {
      const formatedWorkflowList: FormatedWorkflowList[] = response.map((workflow) => ({
        id: workflow.id,
        title: workflow.title,
        updated_by_details: workflow.updated_by_details,
      }));
      yield call(callback.onSuccess, formatedWorkflowList);
    } else if (response && !callback) {
      yield put(getWtfWorkflowSuccessAction({ data: response, count: count }));
    }
  } catch (error) {
    showWorkflowErrorToast();
    yield put(getWtfWorkflowErrorAction(error as string | Error));
  }
}

export function* getWorkflowCandidateListSaga(_action: GetWorkFlowCandidateList) {
  const { workflow_id, step_id } = _action.payload;
  const queryVariables = {
    conditions: {
      workflow_id: { _eq: workflow_id },
      workflow_candidate_steps: { step_id: { _eq: step_id } },
    },
  };
  try {
    const response: WorkflowCandidateList[] =
      // @ts-ignore - redux-saga types
      yield call(fetchData, {
        queryString: GET_WTF_CANDIDATE_LIST_QUERY,
        queryKey: 'wft_workflow_candidate',
        queryVariables: queryVariables,
        forceRefresh: true,
      });
    yield put(getWorkflowCandidateListSuccessAction({ data: response }));
  } catch (error) {
    showWorkflowErrorToast();
    yield put(getWorkflowCandidateListErrorAction(error as string | Error));
  }
}

export function* workFlowListSaga() {
  yield takeEvery(getWtfWorkflowAction.type, getWorkFlowListSaga);
}

export function* workflowCandidateListSaga() {
  yield takeLatest(getWorkflowCandidateListAction.type, getWorkflowCandidateListSaga);
}

export function* workFlowSaga() {
  yield all([workFlowListSaga(), workflowCandidateListSaga()]);
}
